import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { FieldsService } from '../../../services/fields.service';
import { PrintService } from '../../../services/print.service';
import { WeekYearChangeEvent } from '../../shared/week-year-selector/week-year-selector.component';

type Scope = 'studio' | 'staff';
type CountBasis = 'scheduled' | 'taken';

interface ReportRow {
  key: string;
  label: string;
  category: 'lesson' | 'group_lesson' | 'services' | 'schedules' | string;
}

interface ReportGrid {
  instructor_id: number | string | null;
  instructor_name: string | null;
  cells: Record<string, Record<string, number>>;
  daily_totals: Record<string, number>;
  grand_total: number;
  /** Events whose attendees are all Cancelled / Rescheduled, kept out of `cells`. */
  cancelled_cells?: Record<string, Record<string, number>>;
  daily_cancelled_totals?: Record<string, number>;
  grand_total_cancelled?: number;
}

interface ReportResponse {
  week_start: string;
  week_end: string;
  scope: Scope;
  count_basis: CountBasis;
  rows: ReportRow[];
  grids: ReportGrid[];
}

@Component({
  selector: 'app-weekly-schedule-totals',
  templateUrl: './weekly-schedule-totals.component.html',
})
export class WeeklyScheduleTotalsComponent implements OnInit {
  week: string = '';
  year: string = '';
  scope: Scope = 'studio';
  countBasis: CountBasis = 'scheduled';

  loading = false;
  errorMessage: string | null = null;
  response: ReportResponse | null = null;

  // Sunday-first to match the screenshot. Indices match PHP's date('w').
  readonly dayKeys: string[] = ['0', '1', '2', '3', '4', '5', '6'];
  readonly dayLabels: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  readonly displayedColumns: string[] = ['category', '0', '1', '2', '3', '4', '5', '6', 'total'];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authService: AuthService,
    private _drupalRESTService: DrupalRESTService,
    private _fieldsService: FieldsService,
    private printService: PrintService,
  ) {}

  ngOnInit(): void {
    const params = this._activatedRoute.snapshot.queryParams;
    this.year = params['year'] || moment().weekYear().toString();
    this.week = params['week'] || this._fieldsService.getCurrentWeek().toString();
    this.fetch();
  }

  onWeekYearChange(event: WeekYearChangeEvent): void {
    this.week = event.week;
    this.year = event.year;
    this.fetch();
  }

  onScopeChange(value: Scope): void {
    this.scope = value;
    this.fetch();
  }

  onCountBasisChange(value: CountBasis): void {
    this.countBasis = value;
    this.fetch();
  }

  fetch(): void {
    const qs = this.buildQueryString();
    if (qs === null) return;

    this.loading = true;
    this.errorMessage = null;
    this._drupalRESTService.httpGET(`/api/v1/weekly-schedule-totals?${qs}`).subscribe(
      (data: ReportResponse) => {
        this.response = data;
        this.loading = false;
      },
      (err: any) => {
        this.loading = false;
        this.errorMessage = err?.error?.message || 'Failed to load report.';
      },
    );
  }

  exportReport(): void {
    const qs = this.buildQueryString();
    if (qs === null) return;
    const url = `${environment.hostUrl}/api/v1/weekly-schedule-totals/export?${qs}`;
    window.open(url, '_blank');
  }

  printReport(): void {
    if (!this.response) return;

    const scopeLabel = this.scope === 'staff' ? 'Per Staff' : 'Studio Total';
    const basisLabel = this.countBasis === 'taken' ? 'Taken' : 'Scheduled';
    const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const dayHeaders = ['Daily Count (Sun)', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const headerRowHtml = `
      <tr>
        <th>Category</th>
        ${dayHeaders.map(d => `<th>${d}</th>`).join('')}
        <th>Total</th>
      </tr>
    `;

    const gridHtml = (grid: ReportGrid): string => {
      // Same stacked cell as the screen: count, then a small muted
      // "n cancelled" line when there is one.
      const stacked = (count: number, cancelled: number, blankZero: boolean, bold = false): string => {
        const text = blankZero ? this.countText(count, cancelled) : `${count}`;
        const main = bold && text !== '' ? `<strong>${text}</strong>` : text;
        const sub = cancelled > 0 ? `<small class="cancelled">${this.cancelledText(cancelled)}</small>` : '';
        return main + sub;
      };

      const bodyRows = this.visibleRows(grid).map(row => `
        <tr>
          <td>${escape(row.label)}</td>
          ${this.dayKeys.map(d => `<td class="num">${stacked(this.cell(grid, row.key, d), this.cancelledCell(grid, row.key, d), true)}</td>`).join('')}
          <td class="num">${stacked(this.rowTotal(grid, row.key), this.cancelledRowTotal(grid, row.key), true, true)}</td>
        </tr>
      `).join('');

      const totalsRow = `
        <tr class="totals">
          <td><strong>Daily Total Lessons</strong></td>
          ${this.dayKeys.map(d => `<td class="num">${stacked(this.dailyLessonTotal(grid, d), this.dailyLessonCancelled(grid, d), false, true)}</td>`).join('')}
          <td class="num">${stacked(this.lessonGrandTotal(grid), this.lessonGrandCancelled(grid), false, true)}</td>
        </tr>
      `;

      const cancelledNote = grid.grand_total_cancelled
        ? ` &middot; ${this.cancelledText(grid.grand_total_cancelled)}`
        : '';
      const heading = grid.instructor_name
        ? `<h3>${escape(grid.instructor_name)} <small>(${grid.grand_total} events${cancelledNote})</small></h3>`
        : '';

      return `
        <div class="section">
          ${heading}
          <table>
            <thead>${headerRowHtml}</thead>
            <tbody>${bodyRows}${totalsRow}</tbody>
          </table>
        </div>
      `;
    };

    const content = `
      <h2>Weekly Schedule Totals</h2>
      <p>Week ${this.week}, ${this.year} (${this.response.week_start} – ${this.response.week_end}) &middot; ${scopeLabel} &middot; ${basisLabel}</p>
      ${this.response.grids.map(gridHtml).join('')}
    `;

    const customStyles = `
      .section { margin-bottom: 20px; page-break-inside: avoid; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
      small.cancelled { display: block; color: #6c757d; white-space: nowrap; }
      th { background: #f2f2f2; }
      td.num { text-align: center; }
      tr.totals td { background: #f9f9f9; }
    `;

    this.printService.print(content, {
      title: 'Weekly Schedule Totals',
      styles: customStyles,
      showSchoolName: true,
      promptPrint: true,
    });
  }

  /**
   * Compose the shared query string used by both fetch() and exportReport().
   * Returns null when prerequisites are missing (no studio selected, no week/year).
   */
  private buildQueryString(): string | null {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.errorMessage = 'No studio selected.';
      return null;
    }
    if (!this.week || !this.year) {
      return null;
    }
    const range = this._fieldsService.getWeekDateRange(+this.year, +this.week);
    return [
      `field_studio_reference=${studioId}`,
      `week_start=${range.startDate}`,
      `week_end=${range.endDate}`,
      `scope=${this.scope}`,
      `count_basis=${this.countBasis}`,
    ].join('&');
  }

  // ---------- View helpers ----------

  get lessonRows(): ReportRow[] {
    return (this.response?.rows ?? []).filter(r => r.category === 'lesson');
  }

  get bundleRows(): ReportRow[] {
    return (this.response?.rows ?? []).filter(r => r.category !== 'lesson');
  }

  /** Rows for a specific grid with any activity this week, cancelled / rescheduled included. */
  visibleRows(grid: ReportGrid): ReportRow[] {
    const allRows = this.lessonRows.concat(this.bundleRows);
    return allRows.filter(row => this.rowTotal(grid, row.key) > 0 || this.cancelledRowTotal(grid, row.key) > 0);
  }

  /**
   * The count as shown in a body cell. Blank only when nothing happened at all;
   * a "0" stays when a cancelled line follows so that line is not orphaned.
   */
  countText(count: number, cancelled: number): string {
    return count === 0 && cancelled === 0 ? '' : `${count}`;
  }

  /** The small line under a count: "2 cancelled". Covers Cancelled and Rescheduled attendees. */
  cancelledText(n: number): string {
    return `${n} cancelled`;
  }

  cell(grid: ReportGrid, rowKey: string, day: string): number {
    return grid.cells?.[rowKey]?.[day] ?? 0;
  }

  cancelledCell(grid: ReportGrid, rowKey: string, day: string): number {
    return grid.cancelled_cells?.[rowKey]?.[day] ?? 0;
  }

  rowTotal(grid: ReportGrid, rowKey: string): number {
    return grid.cells?.[rowKey]?.['total'] ?? 0;
  }

  cancelledRowTotal(grid: ReportGrid, rowKey: string): number {
    return grid.cancelled_cells?.[rowKey]?.['total'] ?? 0;
  }

  dailyLessonTotal(grid: ReportGrid, day: string): number {
    let sum = 0;
    for (const row of this.lessonRows) {
      sum += this.cell(grid, row.key, day);
    }
    return sum;
  }

  dailyLessonCancelled(grid: ReportGrid, day: string): number {
    let sum = 0;
    for (const row of this.lessonRows) {
      sum += this.cancelledCell(grid, row.key, day);
    }
    return sum;
  }

  lessonGrandTotal(grid: ReportGrid): number {
    let sum = 0;
    for (const row of this.lessonRows) {
      sum += this.rowTotal(grid, row.key);
    }
    return sum;
  }

  lessonGrandCancelled(grid: ReportGrid): number {
    let sum = 0;
    for (const row of this.lessonRows) {
      sum += this.cancelledRowTotal(grid, row.key);
    }
    return sum;
  }
}
